import { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Users, Mail, Shield, ShieldCheck, Building, Trash2, UserCog, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const getBackendUrl = () => {
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getBackendUrl();

function AdminUsers() {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [updatingUser, setUpdatingUser] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, companiesRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/users`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/admin/companies`, { credentials: 'include' })
      ]);

      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data);
      }
      
      if (companiesRes.ok) {
        const data = await companiesRes.json();
        setCompanies(data);
      }
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    setUpdatingUser(userId);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role: newRole })
      });

      if (response.ok) {
        toast.success(`User role updated to ${newRole}`);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update role');
      }
    } catch (error) {
      toast.error('Failed to update role');
    } finally {
      setUpdatingUser(null);
    }
  };

  const handleCompanyAssign = async (userId, companyId) => {
    setUpdatingUser(userId);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}/company`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ company_id: companyId || null })
      });

      if (response.ok) {
        toast.success('Company assignment updated');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to assign company');
      }
    } catch (error) {
      toast.error('Failed to assign company');
    } finally {
      setUpdatingUser(null);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('User deleted successfully');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete user');
      }
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const getUserCompany = (userId) => {
    return companies.find(c => c.user_id === userId);
  };

  const adminCount = users.filter(u => u.role === 'admin').length;
  const customerCount = users.filter(u => u.role === 'customer').length;

  if (loading) {
    return (
      <AdminLayout title="User Management">
        <div className="flex items-center justify-center h-64">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-secondary border-r-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="User Management">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <UserCog className="h-8 w-8 text-secondary" />
          <div>
            <h2 className="text-2xl font-mono font-bold">User Management</h2>
            <p className="text-sm text-muted-foreground">Manage user roles and permissions</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8 text-muted-foreground" />
              <div>
                <p className="text-2xl font-mono font-bold">{users.length}</p>
                <p className="text-sm text-muted-foreground font-mono">Total Users</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4 border-l-4 border-l-secondary">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-8 w-8 text-secondary" />
              <div>
                <p className="text-2xl font-mono font-bold">{adminCount}</p>
                <p className="text-sm text-muted-foreground font-mono">Administrators</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4 border-l-4 border-l-primary">
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-mono font-bold">{customerCount}</p>
                <p className="text-sm text-muted-foreground font-mono">Customers</p>
              </div>
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-secondary/10 border border-secondary/20 rounded-lg p-4">
          <p className="text-sm text-muted-foreground">
            <span className="text-secondary font-bold">Note:</span> Users are automatically created when they sign in with Google. 
            You can change their role to <span className="font-mono text-secondary">Admin</span> to give them access to the admin portal, 
            or assign them to a <span className="font-mono text-primary">Company</span> to link their customer data.
          </p>
        </div>

        {/* Users List */}
        {users.length === 0 ? (
          <div className="bg-card border border-border rounded-lg p-12 text-center">
            <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-mono font-bold mb-2">No Users Yet</h3>
            <p className="text-muted-foreground">Users will appear here after they sign in</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/10 border-b border-border">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase tracking-wider">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase tracking-wider">Assigned Company</th>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase tracking-wider">Joined</th>
                    <th className="px-6 py-3 text-right text-xs font-mono font-bold uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {users.map((u) => {
                    const userCompany = getUserCompany(u.user_id);
                    const isUpdating = updatingUser === u.user_id;
                    
                    return (
                      <tr key={u.user_id} className="hover:bg-muted/5">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                              {u.role === 'admin' ? (
                                <ShieldCheck className="h-5 w-5 text-secondary" />
                              ) : (
                                <Users className="h-5 w-5 text-primary" />
                              )}
                            </div>
                            <div>
                              <p className="font-mono font-bold">{u.name || 'Unknown'}</p>
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Mail className="h-3 w-3" />
                                {u.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {isUpdating ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <select
                                value={u.role}
                                onChange={(e) => handleRoleChange(u.user_id, e.target.value)}
                                className={`px-3 py-1.5 rounded-md text-sm font-mono border ${
                                  u.role === 'admin' 
                                    ? 'bg-secondary/10 border-secondary/30 text-secondary' 
                                    : 'bg-primary/10 border-primary/30 text-primary'
                                }`}
                              >
                                <option value="customer">Customer</option>
                                <option value="admin">Admin</option>
                              </select>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {u.role === 'customer' ? (
                            <select
                              value={userCompany?.company_id || ''}
                              onChange={(e) => handleCompanyAssign(u.user_id, e.target.value)}
                              disabled={isUpdating}
                              className="w-full max-w-[200px] px-3 py-1.5 bg-background border border-border rounded-md text-sm font-mono"
                            >
                              <option value="">No company assigned</option>
                              {companies.map(c => (
                                <option key={c.company_id} value={c.company_id}>
                                  {c.name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-sm text-muted-foreground font-mono">N/A (Admin)</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm font-mono text-muted-foreground">
                            {new Date(u.created_at).toLocaleDateString('en-GB')}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteUser(u.user_id, u.name || u.email)}
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              disabled={isUpdating}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminUsers;
